// Sessions.js - Mobile Sessions List Page with MGB medical color scheme
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  initializeUserSessions,
  supabase,
  canAccessSession,
} from '../../supabase';
import MainLayout from './MainLayout';

const Sessions = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch session data with real unlock logic - matching Dashboard metadata
  const fetchSessionData = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      console.log('🔄 Sessions Page: Fetching session data for user:', user.id);

      // Initialize user sessions first
      await initializeUserSessions(user.id);

      // Fetch sessions with detailed progress data
      const { data: sessionsData, error } = await supabase
        .from('user_sessions')
        .select(
          `
          session_number,
          session_completed,
          session_started,
          video_watched,
          video_completed,
          quiz_completed,
          pain_level,
          pain_level_timestamp,
          goals_set_timestamp,
          weekly_goals,
          next_session_unlock_time,
          quiz_score,
          video_progress_percentage,
          created_at
        `,
        )
        .eq('user_id', user.id)
        .order('session_number');

      if (error) {
        console.error('❌ Sessions Page: Error fetching sessions:', error);
        throw new Error(error.message);
      }

      // Session metadata - EXACT MATCH with Dashboard
      const sessionMetadata = [
        {
          id: 'session0',
          number: 0,
          title: 'Welcome & Program Introduction',
          subtitle: 'Get oriented with the program and set expectations.',
          estimatedTime: '15 min',
        },
        {
          id: 'session1',
          number: 1,
          title: 'Taking Charge of Orofacial Pain',
          subtitle:
            'Understand chronic pain and practice deep breathing techniques.',
          estimatedTime: '20 min',
        },
        {
          id: 'session2',
          number: 2,
          title: 'Managing Emotional Challenges in Orofacial Pain',
          subtitle:
            'Break negative pain spirals with mindfulness and body scan practice.',
          estimatedTime: '23 min',
        },
        {
          id: 'session3',
          number: 3,
          title: 'Working with Negative Thoughts in Orofacial Pain',
          subtitle:
            'Challenge negative thoughts using catch, check, and change techniques.',
          estimatedTime: '19 min',
        },
        {
          id: 'session4',
          number: 4,
          title: 'Not Letting Orofacial Pain Get the Best of Us',
          subtitle:
            'Practice acceptance and re-engage in meaningful life activities.',
          estimatedTime: '22 min',
        },
        {
          id: 'session5',
          number: 5,
          title:
            'Facing Forward: Rolling with Orofacial Pain & Staying Resilient',
          subtitle:
            'Maintain progress and build long-term resilience strategies.',
          estimatedTime: '22 min',
        },
      ];

      // Use the same backend gate as route protection to avoid UI/route mismatches.
      const transformedSessions = await Promise.all(
        sessionMetadata.map(async (metadata) => {
          const sessionData = sessionsData?.find(
            (s) => s.session_number === metadata.number,
          );
          let isUnlocked = false;

          try {
            isUnlocked = await canAccessSession(user.id, metadata.number);
          } catch (accessError) {
            console.error(
              `❌ Sessions Page: access check failed for session ${metadata.number}:`,
              accessError,
            );
            isUnlocked = false;
          }

          // Real progress tracking based on actual database values - EXACT from Dashboard
          const progress = {
            // Pain Log: completed if pain level has been recorded
            painLog:
              sessionData?.pain_level !== null &&
              sessionData?.pain_level !== undefined,

            // Goal Setting: completed if weekly goals have been set
            goalSetting:
              sessionData?.weekly_goals !== null &&
              sessionData?.weekly_goals !== undefined &&
              sessionData?.weekly_goals !== '',

            // Video: completed if video has been watched/completed
            video:
              sessionData?.video_watched ||
              sessionData?.video_completed ||
              false,

            // Quiz: completed if quiz has been completed
            quiz: sessionData?.quiz_completed || false,
          };

          return {
            ...metadata,
            completed: sessionData?.session_completed || false,
            unlocked: isUnlocked,
            progress: progress,
            sessionStarted: sessionData?.session_started || false,
            videoWatched: sessionData?.video_watched || false,
            quizCompleted: sessionData?.quiz_completed || false,
            painLevel: sessionData?.pain_level,
            quizScore: sessionData?.quiz_score,
            videoProgress: sessionData?.video_progress_percentage || 0,
          };
        }),
      );

      console.log('✅ Sessions Page: Sessions loaded:', transformedSessions);
      setSessions(transformedSessions);
    } catch (error) {
      console.error("❌ Sessions Page: Error fetching session data:", error);
      setError(error.message || "Failed to load sessions");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      fetchSessionData();
    }
  }, [user?.id, fetchSessionData]);

  const handleSessionClick = (session) => {
    if (session.unlocked) {
      const sessionNumber = session.id.replace("session", "");
      const formattedSessionId = sessionNumber.padStart(2, "0");
      navigate(`/session${formattedSessionId}`);
    }
  };

  // Helper function to determine if session is started (in progress) - EXACT from Dashboard
  const isSessionStarted = (session) => {
    return session.progress?.painLog || 
           session.progress?.goalSetting || 
           session.progress?.video || 
           session.progress?.quiz;
  };

  if (loading) {
    return (
      <MainLayout>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#F8F9FA'
        }}>
          <div style={{
            padding: '40px',
            backgroundColor: '#FFFFFF',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            textAlign: 'center',
            border: '1px solid #E5E7EB'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid #f1f3f4',
              borderTop: '3px solid #1B365D',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px'
            }} />
            <p style={{ 
              fontFamily: 'Roboto, Arial, sans-serif', 
              color: '#374151' 
            }}>
              Loading Sessions...
            </p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#F8F9FA'
        }}>
          <div style={{
            padding: '40px',
            backgroundColor: '#FFFFFF',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            textAlign: 'center',
            maxWidth: '500px',
            border: '1px solid #E5E7EB'
          }}>
            <h2 style={{ color: '#dc3545', marginBottom: '20px' }}>Error Loading Sessions</h2>
            <p style={{ marginBottom: '20px' }}>{error}</p>
            <button 
              onClick={fetchSessionData}
              style={{
                padding: '10px 20px',
                backgroundColor: '#1B365D',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Try Again
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: '#F8F9FA',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: isMobile ? '40px 20px 16px 20px' : '40px 40px 24px 40px',
            backgroundColor: '#F8F9FA',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: isMobile ? 'flex-start' : 'center',
              marginBottom: isMobile ? '0' : '16px',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? '0' : '8px',
            }}
          >
            {isMobile ? (
              // Mobile Layout - Logo and Sessions on one line
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  width: '100%',
                }}
              >
                {/* Logo Section */}
                <div
                  style={{
                    width: 'calc(40% - 8px)',
                    height: '82px',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    flexShrink: 0,
                  }}
                >
                  <img
                    src="/assets/FFWLOGO.png"
                    alt="Face Forward Web Logo"
                    style={{
                      width: '80%',
                      filter:
                        'brightness(0) saturate(100%) invert(11%) sepia(66%) saturate(1347%) hue-rotate(202deg) brightness(93%) contrast(107%)',
                      height: '60%',
                      objectFit: 'contain',
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  {/* SVG fallback */}
                  <svg
                    width="40"
                    height="25"
                    fill="#1B365D"
                    viewBox="0 0 24 24"
                    style={{ display: 'none' }}
                  >
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                  </svg>
                </div>

                {/* Sessions Title - Takes remaining width */}
                <div
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '16px',
                    padding: '20px 24px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                    height: '82px',
                  }}
                >
                  <h1
                    style={{
                      fontSize: '32px',
                      fontWeight: '600',
                      color: '#1B365D',
                      margin: 0,
                      letterSpacing: '-1px',
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                    }}
                  >
                    Sessions
                  </h1>
                </div>
              </div>
            ) : (
              // Desktop Layout
              <>
                <button
                  onClick={() => navigate('/dashboard')}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#6B7280',
                    cursor: 'pointer',
                    fontSize: '24px',
                    padding: '8px',
                    marginRight: '8px',
                    borderRadius: '8px',
                  }}
                >
                  ←
                </button>
                <h1
                  style={{
                    fontSize: '48px',
                    fontWeight: '600',
                    color: '#1B365D',
                    margin: 0,
                    letterSpacing: '-1px',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                  }}
                >
                  Sessions
                </h1>
              </>
            )}
          </div>
          {/* Desktop subtitle */}
          {!isMobile && (
            <p
              style={{
                fontSize: '16px',
                fontWeight: '400',
                color: '#6B7280',
                margin: '0',
                fontFamily: 'system-ui, -apple-system, sans-serif',
              }}
            >
              Track your progress through the orofacial pain management program
            </p>
          )}
        </div>

        {/* Sessions List */}
        <div
          style={{
            padding: '20px',
            paddingBottom: '100px', // Extra padding for mobile nav
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            {sessions.map((session, index) => (
              <div
                key={session.id}
                style={{
                  backgroundColor: session.completed
                    ? '#ECFDF5'
                    : session.unlocked
                      ? isMobile && !isSessionStarted(session)
                        ? '#FFFFFF'
                        : '#EFF6FF'
                      : '#F9FAFB',
                  borderRadius: '12px',
                  padding: isMobile ? '20px' : '20px',
                  cursor: session.unlocked ? 'pointer' : 'default',
                  opacity: session.unlocked ? 1 : 0.6,
                  transition: 'all 0.2s ease',
                  border: session.completed
                    ? '1px solid #10B981'
                    : session.unlocked
                      ? '1px solid #3B82F6'
                      : '1px solid #E5E7EB',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  position: 'relative',
                }}
                onClick={() => handleSessionClick(session)}
              >
                {isMobile ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px',
                      width: '100%',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '16px',
                        width: '100%',
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: '#1B365D',
                          color: '#FFFFFF',
                          borderRadius: '50%',
                          padding: '12px',
                          fontSize: '16px',
                          fontWeight: '600',
                          minWidth: '48px',
                          height: '48px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {session.number === 0 ? (
                          <svg
                            width="20"
                            height="20"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                          </svg>
                        ) : (
                          session.number
                        )}
                      </div>

                      <div
                        style={{
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        <h4
                          style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            color: '#374151',
                            margin: '0 0 4px 0',
                            lineHeight: '1.3',
                          }}
                        >
                          {session.title}
                        </h4>
                        <p
                          style={{
                            fontSize: '14px',
                            color: '#6B7280',
                            margin: '0',
                            lineHeight: '1.4',
                          }}
                        >
                          {session.subtitle}
                        </p>
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          marginTop: '4px',
                        }}
                      >
                        {session.completed ? (
                          <svg
                            width="24"
                            height="24"
                            fill="#10B981"
                            viewBox="0 0 24 24"
                          >
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                          </svg>
                        ) : session.unlocked ? (
                          isSessionStarted(session) ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              height="24px"
                              viewBox="0 -960 960 960"
                              width="24px"
                              fill="#1B365D"
                            >
                              <path d="M480-80q-82 0-155-31.5t-127.5-86Q143-252 111.5-325T80-480q0-83 31.5-155.5t86-127Q252-817 325-848.5T480-880q17 0 28.5 11.5T520-840q0 17-11.5 28.5T480-800q-133 0-226.5 93.5T160-480q0 133 93.5 226.5T480-160q133 0 226.5-93.5T800-480q0-17 11.5-28.5T840-520q17 0 28.5 11.5T880-480q0 82-31.5 155t-86 127.5q-54.5 54.5-127 86T480-80Z" />
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              height="24px"
                              viewBox="0 -960 960 960"
                              width="24px"
                              fill="#00A0B0"
                            >
                              <path d="M240-160h480v-400H240v400Zm240-120q33 0 56.5-23.5T560-360q0-33-23.5-56.5T480-440q-33 0-56.5 23.5T400-360q0 33 23.5 56.5T480-280ZM240-160v-400 400Zm0 80q-33 0-56.5-23.5T160-160v-400q0-33 23.5-56.5T240-640h280v-80q0-83 58.5-141.5T720-920q83 0 141.5 58.5T920-720h-80q0-50-35-85t-85-35q-50 0-85 35t-35 85v80h120q33 0 56.5 23.5T800-560v400q0 33-23.5 56.5T720-80H240Z" />
                            </svg>
                          )
                        ) : (
                          <svg
                            width="24"
                            height="24"
                            fill="#9ca3af"
                            viewBox="0 0 24 24"
                          >
                            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                          </svg>
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        flexWrap: 'wrap',
                        gap: '8px',
                        width: '100%',
                      }}
                    >
                      {(session.number === 0
                        ? [
                            // No badges for introduction session
                          ]
                        : session.number === 1
                          ? [
                              {
                                name: 'Video',
                                completed: session.progress?.video,
                              },
                              {
                                name: 'Quiz',
                                completed: session.progress?.quiz,
                              },
                            ]
                          : [
                              {
                                name: 'Pre-Assessment',
                                completed: session.progress?.goalSetting,
                              },
                              {
                                name: 'Video',
                                completed: session.progress?.video,
                              },
                              {
                                name: 'Quiz',
                                completed: session.progress?.quiz,
                              },
                            ]
                      ).map((tag, tagIndex) => (
                        <div
                          key={tagIndex}
                          style={{
                            backgroundColor: '#F9FAFB',
                            color: '#374151',
                            padding: '6px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            border: '1px solid #E5E7EB',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                          }}
                        >
                          {tag.name}
                          {tag.completed && (
                            <svg
                              width="12"
                              height="12"
                              fill="#10B981"
                              viewBox="0 0 24 24"
                            >
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                            </svg>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '16px',
                      width: '100%',
                      maxWidth: '100%',
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: '#1B365D',
                        color: '#FFFFFF',
                        borderRadius: '50px',
                        padding: '8px 16px',
                        fontSize: '14px',
                        fontWeight: '400',
                        minWidth: session.number === 0 ? '60px' : '90px',
                        textAlign: 'center',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                      }}
                    >
                      {session.number === 0 ? (
                        <svg
                          width="16"
                          height="16"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                      ) : (
                        `Session ${session.number}`
                      )}
                    </div>

                    <div
                      style={{
                        flex: 1,
                        minWidth: 0,
                        maxWidth: '100%',
                        overflow: 'hidden',
                      }}
                    >
                      <h4
                        style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#374151',
                          margin: '0 0 4px 0',
                        }}
                      >
                        {session.title}
                      </h4>
                      <p
                        style={{
                          fontSize: '14px',
                          color: '#6B7280',
                          margin: '0 0 12px 0',
                        }}
                      >
                        {session.subtitle}
                      </p>

                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '8px',
                        }}
                      >
                        {(session.number === 0
                          ? [
                              // No badges for introduction session
                            ]
                          : session.number === 1
                            ? [
                                {
                                  name: 'Video',
                                  completed: session.progress?.video,
                                },
                                {
                                  name: 'Quiz',
                                  completed: session.progress?.quiz,
                                },
                              ]
                            : [
                                {
                                  name: 'Pre-Assessment',
                                  completed: session.progress?.goalSetting,
                                },
                                {
                                  name: 'Video',
                                  completed: session.progress?.video,
                                },
                                {
                                  name: 'Quiz',
                                  completed: session.progress?.quiz,
                                },
                              ]
                        ).map((tag, tagIndex) => (
                          <div
                            key={tagIndex}
                            style={{
                              backgroundColor: '#F9FAFB',
                              color: '#374151',
                              padding: '4px 8px',
                              borderRadius: '50px',
                              fontSize: '12px',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              border: '1px solid #E5E7EB',
                            }}
                          >
                            {tag.name}
                            {tag.completed && (
                              <svg
                                width="12"
                                height="12"
                                fill="#10B981"
                                viewBox="0 0 24 24"
                              >
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                              </svg>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <button
                      style={{
                        backgroundColor: '#F9FAFB',
                        color: '#374151',
                        border: '1px solid #E5E7EB',
                        borderRadius: '20px',
                        padding: '8px 16px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: session.unlocked ? 'pointer' : 'default',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {session.completed ? (
                        <>
                          Complete
                          <svg
                            width="12"
                            height="12"
                            fill="#10B981"
                            viewBox="0 0 24 24"
                          >
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                          </svg>
                        </>
                      ) : session.unlocked ? (
                        isSessionStarted(session) ? (
                          <>
                            In Progress
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              height="12px"
                              viewBox="0 -960 960 960"
                              width="12px"
                              fill="#1B365D"
                            >
                              <path d="M480-80q-82 0-155-31.5t-127.5-86Q143-252 111.5-325T80-480q0-83 31.5-155.5t86-127Q252-817 325-848.5T480-880q17 0 28.5 11.5T520-840q0 17-11.5 28.5T480-800q-133 0-226.5 93.5T160-480q0 133 93.5 226.5T480-160q133 0 226.5-93.5T800-480q0-17 11.5-28.5T840-520q17 0 28.5 11.5T880-480q0 82-31.5 155t-86 127.5q-54.5 54.5-127 86T480-80Z" />
                            </svg>
                          </>
                        ) : (
                          <>
                            Unlocked
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              height="12px"
                              viewBox="0 -960 960 960"
                              width="12px"
                              fill="#00A0B0"
                            >
                              <path d="M240-160h480v-400H240v400Zm240-120q33 0 56.5-23.5T560-360q0-33-23.5-56.5T480-440q-33 0-56.5 23.5T400-360q0 33 23.5 56.5T480-280ZM240-160v-400 400Zm0 80q-33 0-56.5-23.5T160-160v-400q0-33 23.5-56.5T240-640h280v-80q0-83 58.5-141.5T720-920q83 0 141.5 58.5T920-720h-80q0-50-35-85t-85-35q-50 0-85 35t-35 85v80h120q33 0 56.5 23.5T800-560v400q0 33-23.5 56.5T720-80H240Z" />
                            </svg>
                          </>
                        )
                      ) : (
                        <>
                          Locked
                          <svg
                            width="12"
                            height="12"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                          </svg>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <style
          dangerouslySetInnerHTML={{
            __html: `
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `,
          }}
        />
      </div>
    </MainLayout>
  );
};

export default Sessions;